import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { CURRENT_BUILD_VERSION } from "../buildVersion";

const CHECK_INTERVAL_MS = 60 * 1000;

export default function AppUpdatePrompt() {
  const [latestVersion, setLatestVersion] = useState(null);
  const checking = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (CURRENT_BUILD_VERSION === "development" || checking.current) return;

    checking.current = true;
    try {
      const response = await fetch(
        `/version.json?checkedAt=${Date.now()}`,
        { cache: "no-store", headers: { "Cache-Control": "no-cache" } },
      );
      if (!response.ok) return;

      const data = await response.json();
      if (data?.version && data.version !== CURRENT_BUILD_VERSION) {
        setLatestVersion(String(data.version));
      }
    } catch (_) {
      // A temporary network failure should not interrupt the active session.
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    const handleFocus = () => checkForUpdate();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    checkForUpdate();

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkForUpdate]);

  const refreshApp = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_appv", latestVersion || Date.now());
    window.location.replace(url.toString());
  };

  return (
    <Snackbar
      open={Boolean(latestVersion)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ mb: { xs: 1, sm: 2 }, maxWidth: "calc(100% - 24px)" }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ width: "100%", alignItems: "center", borderRadius: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={refreshApp}
            sx={{ whiteSpace: "nowrap", fontWeight: 800 }}
          >
            Refresh
          </Button>
        }
      >
        A new app update is available.
      </Alert>
    </Snackbar>
  );
}
